import { Button, SegmentedControl } from "@mantine/core";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Service } from "@turizi/shared";
import { useServiceFilters } from "@/hooks/use-service-filters";
import { useServices } from "@/hooks/api-hooks";
import ServiceCardCatalog from "@/components/service-card-catalog";
import ServiceFiltersColumn from "@/components/service-tables/service-filters-column";
import ServicesTable from "@/components/services-table";
import { IconFilter, IconX, IconShare, IconCheck } from "@tabler/icons-react";
import { SkeletonListWithFilters } from "@/components/snippets";
import SearchInput from "@/components/search-input";
import { multiWordSearch } from "@/utils/search-utils";
import BulkServiceEditModal from "@/components/service-tables/bulk-service-edit-modal";
import ServicesShareModal from "./services-share-modal";
import { ServiceToString } from "@/lib/models-utils";
import DatePickerCustom from "@/components/custom-date-picker";
import { localTenant } from "@/services/session-service";


const PAGE_SIZE = 2000;


interface Props {
    selectedCategory: string | null;

    // For availability page
    initialDateRange?: [string | null, string | null];
    enableBulkActions?: boolean;
    selectedServices?: string[];
    toggleServiceSelection?: (id: string) => void;
    onSelectService?: (service: Service) => void;
    paxCount?: number;

    // Sync state (mainly for services-list-page URL sync)
    dateRange?: [string | null, string | null];
    onDateRangeChange?: (range: [string | null, string | null]) => void;
    initialFilters?: Record<string, string[]>;
    onFiltersChange?: (filters: Record<string, string[]>) => void;
    tenantId?: number;
    reservationId?: number;
}

export default function ServicesCatalogBrowser({
    selectedCategory,
    initialDateRange = [null, null],
    enableBulkActions = false,
    selectedServices: controlledSelectedServices,
    toggleServiceSelection: controlledToggleServiceSelection,
    onSelectService,
    paxCount = 0,
    dateRange: controlledDateRange,
    onDateRangeChange,
    initialFilters = {},
    onFiltersChange,
    tenantId,
    reservationId,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();
    const tenant_id = tenant?.id;
    const [viewMode, setViewMode] = useState<string>(() => localStorage.getItem('services_view_mode') || 'list');

    const [uncontrolledDateRange, setUncontrolledDateRange] = useState<[string | null, string | null]>(initialDateRange);
    const dateRange = controlledDateRange ?? uncontrolledDateRange;
    const setDateRange = onDateRangeChange ?? setUncontrolledDateRange;

    const [showFiltersMobile, setShowFiltersMobile] = useState(false);
    const [uncontrolledSelectedServices, setUncontrolledSelectedServices] = useState<string[]>([]);
    const selectedServices = controlledSelectedServices ?? uncontrolledSelectedServices;
    const [searchQuery, setSearchQuery] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const {
        isLoading: isLoadingServices,
        data: servicesData
    } = useServices(
        {
            tenant_id: tenantId || tenant_id,
            reservation_id: reservationId,
            category: selectedCategory,
            start_day: dateRange[0],
            end_day: dateRange[1],
        },
        {
            enabled: !!selectedCategory,
            staleTime: 0,
            refetchOnMount: 'always'
        }
    );

    const rawServices = useMemo(() => {
        return (servicesData?.data || []);
    }, [servicesData?.data]);

    const {
        filters,
        toggleFilter,
        filteredServices: servicesFilteredByAttributes,
        allAttributeKeys,
        providerAttributeKeys,
        allFilterOptions,
        availableFilterOptions
    } = useServiceFilters({
        services: rawServices,
        selectedCategory,
        initialFilters
    });

    useEffect(() => {
        onFiltersChange?.(filters);
    }, [filters, onFiltersChange]);

    const filteredServices = useMemo(() => {
        if (searchQuery) {
            return rawServices.filter(service => {
                const title = multiWordSearch(ServiceToString(service, t), searchQuery) || false;
                const _id = service.id.toString() === searchQuery.trim();
                return title || _id;
            });
        }
        return servicesFilteredByAttributes.sort((a, b) => b.is_favorite ? 1 : -1);
    }, [searchQuery, rawServices, servicesFilteredByAttributes, t]);

    const selectAll = () => {
        if (selectedServices?.length === filteredServices.length) {
            setUncontrolledSelectedServices([]);
        } else {
            setUncontrolledSelectedServices(filteredServices.map(s => String(s.id)));
        }
    };

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [selectedCategory, searchQuery, filters]);

    const internalToggleServiceSelection = useCallback((serviceId: string) => {
        setUncontrolledSelectedServices(prev => {
            if (prev.includes(serviceId)) {
                return prev.filter(id => id !== serviceId);
            } else {
                return [...prev, serviceId];
            }
        });
    }, []);

    const toggleServiceSelection = controlledToggleServiceSelection ?? internalToggleServiceSelection;

    return (
        <div className="pb-20">
            <button
                onClick={() => setShowFiltersMobile(!showFiltersMobile)}
                className={`
                    md:hidden w-fit mb-4
                    flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors
                    ${Object.values(filters).some(f => f.length)
                        ? 'border-blue-200 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }
                `}
            >
                {showFiltersMobile ? <IconX size={16} /> : <IconFilter size={16} />}
                <span>{t('filters')}</span>
            </button>


            {showFiltersMobile && (
                <div className="fixed inset-0 bg-black/20 z-40 md:hidden" onClick={() => setShowFiltersMobile(false)} />
            )}

            {/* Content */}
            <div className="space-y-4">

                {isLoadingServices && (
                    <SkeletonListWithFilters />
                )}

                {!isLoadingServices && (
                    <>

                        <div className="flex gap-4">
                            {/* Filters Sidebar */}
                            {!isLoadingServices && !showFiltersMobile && (
                                <div className={`solid-box-border p-0 h-fit`}>
                                    <ServiceFiltersColumn
                                        filters={filters}
                                        allFilterOptions={allFilterOptions}
                                        availableFilterOptions={availableFilterOptions}
                                        allAttributeKeys={allAttributeKeys}
                                        providerAttributeKeys={providerAttributeKeys}
                                        onToggleFilter={toggleFilter}
                                    />
                                </div>
                            )}
                            <div className="w-full min-w-0 space-y-2">
                                <div className="flex flex-wrap gap-2 items-center">
                                    <SearchInput
                                        onSearch={setSearchQuery}
                                        size="xs"
                                    />
                                    <DatePickerCustom
                                        hideLabel
                                        placeholder={t('dates')}
                                        value={dateRange}
                                        onChange={(range: [string | null, string | null]) => setDateRange(range)}
                                        size="xs"
                                        clearable
                                        className="font-bold"
                                    />
                                    <div>
                                        <SegmentedControl
                                            value={viewMode}
                                            onChange={(val: string) => {
                                                localStorage.setItem('services_view_mode', val);
                                                setViewMode(val);
                                            }}
                                            color='black'
                                            size="sm"
                                            data={[
                                                { value: 'table', label: t('table') },
                                                { value: 'list', label: t('list') },
                                            ]}
                                        />
                                    </div>
                                    <div className="text-xs text-gray-400 font-bold">
                                        ({filteredServices.length})
                                    </div>
                                </div>

                                {viewMode === 'table' && (
                                    <ServicesTable
                                        services={filteredServices.slice(0, visibleCount)}
                                        rawServices={rawServices}
                                        selectedCategory={selectedCategory}
                                        enableBulkActions={enableBulkActions}
                                        selectedServices={selectedServices}
                                        toggleServiceSelection={toggleServiceSelection}
                                        onSelectService={onSelectService}
                                        selectAll={selectAll}
                                        start_day={dateRange[0] || undefined}
                                        end_day={dateRange[1] || undefined}
                                        paxCount={paxCount}
                                    />
                                )}
                                {viewMode === 'list' && (
                                    <div className="space-y-3 text-sm">
                                        {filteredServices.slice(0, visibleCount).map((service) => {
                                            const isSelected = selectedServices.includes(String(service.id));
                                            return (
                                                <div key={service.id} className="relative group">
                                                    <ServiceCardCatalog
                                                        service={service}
                                                        onSelect={onSelectService}
                                                        start_day={dateRange[0] || undefined}
                                                        end_day={dateRange[1] || undefined}
                                                        paxCount={paxCount}
                                                        showProviders
                                                        className={`
                                                            transition-all duration-150 
                                                            ${isSelected
                                                                ? 'solid-box-border-positive'
                                                                : 'solid-box-border-hover'}`
                                                        }
                                                    />
                                                    {isSelected && (
                                                        <div className="absolute top-2.5 right-2.5 z-20 flex items-center gap-1 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-2xs pointer-events-none">
                                                            <IconCheck size={12} stroke={3} />
                                                            <span>{t('selected')}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {filteredServices.length > visibleCount && (
                                    <div className="flex justify-center pt-4">
                                        <button onClick={() => setVisibleCount(prev => prev + 20)} className="px-6 py-2 bg-white border border-gray-300 rounded-full shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                                            {t('show_more')}
                                        </button>
                                    </div>
                                )}

                                {filteredServices.length === 0 && (
                                    <div className="text-gray-500 text-center py-10">
                                        {t('no_results')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
